import { type ComponentType, Suspense, lazy, memo } from "react";
import { Route, Routes } from "react-router-dom";
import { ROUTES } from "./config";
import { CircularProgress } from "./components";
import { Redirect } from "./hoc/Redirect";

// Lazy load all pages
const Deposit = lazy(() => import("./pages/deposit"));
const Dev = lazy(() => import("./pages/dev"));
const Lock = lazy(() => import("./pages/lock"));
const Mint = lazy(() => import("./pages/mint"));
// const PointsPage = lazy(() => import("./pages/points/PointsV1Page"));
const Points = lazy(() => import("./pages/points"));
const Portfolio = lazy(() => import("./pages/portfolio"));
const Terms = lazy(() => import("./pages/terms"));
const Vote = lazy(() => import("./pages/vote"));

// Loading component for suspense fallback
const PageLoader = () => (
    <div className="flex items-center justify-center h-screen">
        <CircularProgress size={48} />
    </div>
);

// Suspense wrapper for lazy-loaded routes
const LazyRoute = ({ component: Component }: { component: ComponentType }) => (
    <Suspense fallback={<PageLoader />}>
        <Component />
    </Suspense>
);

type RouteConfig = {
    path: `/${string}`;
    element: JSX.Element;
    sitemap?: boolean;
};

const DEV_ROUTES: RouteConfig[] = import.meta.env.DEV
    ? [{ path: ROUTES.DEV, element: <LazyRoute component={Dev} />, sitemap: false }]
    : [];

const ROUTER_CONFIG: RouteConfig[] = [
    { path: ROUTES.HOME, element: <LazyRoute component={Deposit} />, sitemap: true },
    { path: ROUTES.DEPOSIT, element: <Redirect route={ROUTES.HOME} />, sitemap: false },
    { path: ROUTES.LOCK, element: <LazyRoute component={Lock} />, sitemap: true },
    { path: ROUTES.MINT, element: <LazyRoute component={Mint} />, sitemap: true },
    { path: ROUTES.PORTFOLIO, element: <LazyRoute component={Portfolio} />, sitemap: true },
    { path: ROUTES.VOTE, element: <LazyRoute component={Vote} />, sitemap: true },
    { path: ROUTES.TERMS, element: <LazyRoute component={Terms} />, sitemap: true },
    { path: ROUTES.POINTS, element: <LazyRoute component={Points} />, sitemap: true },
    ...DEV_ROUTES,
];

export const Router = memo(function Router() {
    return (
        <Routes>
            {ROUTER_CONFIG.map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
            ))}
        </Routes>
    );
});

Router.displayName = "Router";
